import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Box, Button, Icon, LoadingMask, Paragraph } from '@zillow/constellation-10';
import { IconPlusFilled } from '@zillow/constellation-icons';

import { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';
import { useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_get } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useFeedOverrideSectionEnabled } from '../../../../hooks/useFeedOverrideSectionEnabled';
import accessUtils from '../../../../utils/accessUtils';

import { REVIEW_PAGE_SECTIONS } from '../../../../constants/ListingDetailConstants';

import CustomDisclaimerModal from '../../../modals/CustomDisclaimerModal';
import MultifamilyCostsAndFeesModal from '../../../modals/MultifamilyCostsAndFeesModal';
import MultifamilyFeedImportedFeeModal from '../../../modals/MultifamilyFeedImportedFeeModal';
import CostsAndFeesFilters from './components/CostsAndFeesFilters';
import CustomDisclaimer from './components/CustomDisclaimer';
import { FeesByCategory } from './components/FeesByCategory';
import MFCostAndFeeTotalMonthlyPriceWithFeeAlert from './components/MFCostAndFeeTotalMonthlyPriceWithFeeAlert';

import {
  emitClickAddFeeEvent,
  emitClickCancelFeeEditEvent,
  EVENT_IDS,
} from '../../../../analytics/events/multiFamilyCostsAndFees';
import { useAuth } from '../../../AuthProvider';
import RecommendationsAlert from '../../../PostingPath/RecommendationsAlert';

import type { MultifamilyFeeDto } from '@zg-rentals/ts-rental-manager-api-client';

const CostsAndFeesContent = () => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { user } = useAuth();

  const { data: listingDetails, isLoading: isFetchingListingDetails } = useGetListingDetails(listingAlias);
  const isFeed = listingDetails?.isFeed;
  const isMultifamily = listingDetails?.isMultifamily;
  const isTotalMonthlyPriceWithFeeEligible = listingDetails?.totalMonthlyPriceWithFeeNotice?.isEligible ?? false;
  const isCostsAndFeesOverrideEnabled = useFeedOverrideSectionEnabled(listingAlias, FeedOverrideSection.COSTS_AND_FEES);

  const [showCostsAndFeesModal, setShowCostsAndFeesModal] = useState(false);
  const [showMultifamilyFeedImportedFeeModal, setShowMultifamilyFeedImportedFeeModal] = useState(false);
  const [showCustomDisclaimerModal, setShowCustomDisclaimerModal] = useState(false);
  const [feeToEdit, setFeeToEdit] = useState<MultifamilyFeeDto | null>(null);

  const { data, isLoading: isCostAndFeesLoading } = useRentalManagerUserPropertyMultifamilyCostAndFeesWebApiHandler_get(
    {
      listingAlias,
    },
    {
      enabled: isMultifamily,
      meta: {
        tags: ['mf-costsAndFees'],
        context: { api: 'rental-manager', details: 'get multifamily costs and fees' },
      },
    },
  );

  const { dictionaryItems, existingFees = {}, disclaimerText } = data || {};

  const isDisabled =
    !user?.features?.canUpdateListing ||
    listingDetails?.isArchived ||
    listingDetails?.restrictions?.isUpdateListingRestricted ||
    (listingDetails?.selfServeOnboardingStatus?.isSelfServeOnboardingInProgress &&
      listingDetails?.selfServeOnboardingStatus?.isUpdateRestricted) ||
    // For feed listings under feed overrides, the section toggle governs all costs & fees editing.
    (isFeed && accessUtils.canAccessFeedOverrides() && !isCostsAndFeesOverrideEnabled);

  const canAccessTotalMonthlyPriceWithFee = accessUtils.canAccessTotalMonthlyPriceWithFee({
    isEligible: isTotalMonthlyPriceWithFeeEligible,
  });
  const handleAddFeeClick = ({ triggerObject }: { triggerObject?: string }) => {
    setShowCostsAndFeesModal(true);
    if (listingDetails && user) {
      emitClickAddFeeEvent({ currentProperty: listingDetails, user, triggerObject });
    }
  };

  const handleAddFeeClickButton = () => {
    setShowCostsAndFeesModal(true);
    if (listingDetails && user) {
      emitClickAddFeeEvent({ currentProperty: listingDetails, user });
    }
  };

  const handleEditFeeClick = (fee: MultifamilyFeeDto) => {
    setFeeToEdit(fee);
    if (fee.isImportedFromFeed && !isCostsAndFeesOverrideEnabled) {
      setShowMultifamilyFeedImportedFeeModal(true);
    } else {
      setShowCostsAndFeesModal(true);
    }
  };

  const addFeeButton = (
    <Button
      onClick={handleAddFeeClickButton}
      icon={<Icon render={<IconPlusFilled />} />}
      data-eventid={EVENT_IDS.CLICK_ADD_MF_FEE}
      disabled={isDisabled}
    >
      Add a fee
    </Button>
  );

  const handleCloseManualFeeModal = ({ isSaving }: { isSaving: boolean }) => {
    setShowCostsAndFeesModal(false);
    setFeeToEdit(null);
    if (!isSaving) {
      if (listingDetails && user) {
        emitClickCancelFeeEditEvent({ currentProperty: listingDetails, user });
      }
    }
  };

  const handleCloseFeedImportedFeeModal = ({ isSaving }: { isSaving: boolean }) => {
    setShowMultifamilyFeedImportedFeeModal(false);
    setFeeToEdit(null);
    if (!isSaving) {
      if (listingDetails && user) {
        emitClickCancelFeeEditEvent({ currentProperty: listingDetails, user });
      }
    }
  };

  return (
    <LoadingMask.Root loading={isFetchingListingDetails || isCostAndFeesLoading}>
      <LoadingMask.Content>
        <div data-testid="CostsAndFeesContent">
          {canAccessTotalMonthlyPriceWithFee && (
            <MFCostAndFeeTotalMonthlyPriceWithFeeAlert handleAddFeeClick={handleAddFeeClick} disabled={isDisabled} />
          )}

          <RecommendationsAlert sectionName={REVIEW_PAGE_SECTIONS.COSTS_AND_FEES} showLearnMore={false} />

          <Box>
            {Object.keys(existingFees).length > 0 && dictionaryItems ? (
              <div data-testid="CostsAndFeesContent-FeesList">
                <Box
                  css={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    gap: '400',
                    paddingBottom: '600',
                  }}
                >
                  <CostsAndFeesFilters dictionaryItems={dictionaryItems} />

                  <Box css={{ flexShrink: 0 }}>{addFeeButton}</Box>
                </Box>

                <FeesByCategory
                  dictionaryItems={dictionaryItems}
                  existingFees={existingFees}
                  handleEditFeeClick={handleEditFeeClick}
                />
              </div>
            ) : (
              <div data-testid="CostsAndFeesContent-Empty">
                {isFeed ? (
                  <>
                    <Paragraph textStyle="body-bold">You haven&apos;t imported or created any fees yet.</Paragraph>

                    <Paragraph css={{ marginBlock: '400' }}>
                      Import fees using your feed, or add them manually here.
                    </Paragraph>
                  </>
                ) : (
                  <Paragraph css={{ marginBottom: '400' }}>You haven&apos;t created any fees yet.</Paragraph>
                )}

                {addFeeButton}
              </div>
            )}

            <Box css={{ marginTop: '800' }}>
              <CustomDisclaimer
                disclaimerText={disclaimerText || null}
                onAddDisclaimerClick={() => setShowCustomDisclaimerModal(true)}
                isDisabled={isDisabled || false}
              />
            </Box>
          </Box>

          {showCostsAndFeesModal && (
            <MultifamilyCostsAndFeesModal onClose={handleCloseManualFeeModal} feeToEdit={feeToEdit} />
          )}

          {showMultifamilyFeedImportedFeeModal && feeToEdit !== null && (
            <MultifamilyFeedImportedFeeModal
              onClose={handleCloseFeedImportedFeeModal}
              feeToEdit={feeToEdit}
              dictionaryItems={dictionaryItems}
            />
          )}

          {showCustomDisclaimerModal && (
            <CustomDisclaimerModal
              disclaimerText={disclaimerText || null}
              setShowCustomDisclaimerModal={setShowCustomDisclaimerModal}
            />
          )}
        </div>
      </LoadingMask.Content>
      <LoadingMask.Spinner />
    </LoadingMask.Root>
  );
};

export default memo(CostsAndFeesContent);
